import type {
  PdfiumApi,
  PdfiumFunctions,
  PdfiumTypeMap,
  Pointer,
} from "@pdfking/pdfium-wrap"
import type { Media, Pixel, SpatialRectangle } from "@pdfking/renderer-api"
import type { Bytes } from "@pdfking/tfunc"
import { PdfiumExports as E } from "@pdfking/pdfium-wrap"

type PointerPage = PdfiumTypeMap["FPDF_PAGE"]

const readBox = async (
  { call, malloc, readFloats }: PdfiumApi,
  page: PointerPage,
  boxType: PdfiumFunctions
): Promise<null | SpatialRectangle<Pixel>> => {
  const { free, pointer } = malloc(16 as Bytes)
  const hasBox = await call(
    boxType,
    page,
    pointer,
    (pointer + 4) as Pointer,
    (pointer + 8) as Pointer,
    (pointer + 12) as Pointer
  )

  if (!hasBox) {
    free()
    return null
  }

  const [left, bottom, right, top] = readFloats(pointer, 4)
  free()

  return {
    height: (top - bottom) as Pixel,
    width: (right - left) as Pixel,
    x: left as Pixel,
    y: bottom as Pixel,
  }
}

export const GetBoxes = async (
  api: PdfiumApi,
  page: PointerPage
): Promise<Media<Pixel>> => {
  const width = (await api.call(E.FPDF_GetPageWidthF, page)) as Pixel
  const height = (await api.call(E.FPDF_GetPageHeightF, page)) as Pixel
  const size = { height, width }

  const defaultBox: SpatialRectangle<Pixel> = {
    x: 0 as Pixel,
    y: 0 as Pixel,
    ...size,
  }

  const media_box =
    (await readBox(api, page, E.FPDFPage_GetMediaBox)) ?? defaultBox
  const trim_box =
    (await readBox(api, page, E.FPDFPage_GetTrimBox)) ?? media_box

  return { media_box, size, trim_box }
}
